import type { RouteLocationNormalizedGeneric } from 'vue-router'

// Returns the redirect target for unauthenticated visitors, or undefined to
// allow. IMPORTANT: the middleware must RETURN navigateTo()'s result — inside
// route middleware, navigateTo() without a return is a silent no-op, which
// once left every "protected" page rendering a broken shell for logged-out
// visitors.
const checkRouteAuth = (to: RouteLocationNormalizedGeneric) => {
  if (import.meta.dev && to.meta.publicHelpPreview === true) return

  switch (true) {
    // Unauthenticated Routes
    case to.path === '/':
    case to.path === '/forgotPassword':
    case to.path === '/login':
    case to.path === '/signup':
    // Post-signup completion page: fresh signups hold no auth cookie yet.
    case to.path === '/signup-complete':
    case to.path === '/reset-password':
    case to.path === '/verify-email':
    case to.path === '/logout':
    case to.path.startsWith('/signup/'):
    case to.path.startsWith('/oidc/'):
    case to.path === '/copilot':
    case to.path.startsWith('/copilot/'):
      return

    case to.path === '/accept-terms': {
      const { clearCookie, isAuthenticated } = useAuth()
      if (!isAuthenticated.value) {
        clearCookie()
        return navigateTo('/login')
      }
      return
    }

    // Any other routes needs authentication
    default: {
      const { clearCookie, isAuthenticated, requiresTermsAcceptance } =
        useAuth()
      if (!isAuthenticated.value) {
        clearCookie()
        return navigateTo('/login')
      }
      if (requiresTermsAcceptance.value) {
        return navigateTo('/accept-terms')
      }
      return
    }
  }
}

export default defineNuxtRouteMiddleware((to) => {
  // Read the auth cookie on the server too, so signed-out requests redirect
  // before rendering a protected layout that cannot hydrate as the login page.
  return checkRouteAuth(to)
})
